import { CalendarDays, MapPin, Users, Video } from 'lucide-react';
import { ResizedImage } from '@ah/design/mobile';
import type { GatheringSeries } from '@ah/gatherings/shared';

function formatInstanceTime(iso: string): string {
  return new Date(iso).toLocaleString('en-GB', {
    weekday: 'short', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit', hour12: true,
  });
}

function isSlotSoldOut(slot: { capacity: number | null; participantCount: number }): boolean {
  return slot.capacity != null && slot.participantCount >= slot.capacity;
}

function seatsLeftLabel(slot: { capacity: number | null; participantCount: number }): string {
  if (isSlotSoldOut(slot)) return 'Sold out';
  return 'A few seats left';
}

export function GatheringSeriesCard({ series }: { series: GatheringSeries }) {
  const isOnline = series.gatheringType.kind === 'online' || series.gatheringType.kind === 'both';
  const isOffline = series.gatheringType.kind === 'offline';
  const upcoming = series.upcomingSessions ?? [];

  return (
    <div className="bg-card rounded-2xl border border-border shadow-sm overflow-hidden">
      {series.imageUrl ? (
        <div className="w-full aspect-video bg-muted overflow-hidden">
          <ResizedImage src={series.imageUrl} renderWidth={384} fallback={false} alt="" className="w-full h-full object-cover" loading="lazy" />
        </div>
      ) : (
        <div className="w-full aspect-video bg-gradient-to-br from-primary/15 to-secondary/25 flex items-center justify-center">
          <CalendarDays size={48} className="text-muted-foreground/30" strokeWidth={1.25} />
        </div>
      )}

      <div className="p-6 space-y-4">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-[11px] uppercase tracking-wider font-semibold text-primary/80 bg-primary/10 rounded-full px-2 py-0.5 inline-flex items-center gap-1">
            <CalendarDays size={9} /> {series.gatheringType.name}
          </span>
          {series.circles.length > 0 && (
            <span className="text-[11px] font-medium text-muted-foreground bg-muted rounded-full px-2 py-0.5 inline-flex items-center gap-1">
              <Users size={9} />
              {series.circles[0].name}
              {series.circles.length > 1 && ` +${series.circles.length - 1} more`}
            </span>
          )}
        </div>

        <div>
          <p className="font-bold text-xl leading-tight">{series.name}</p>
          <p className="mt-1 text-[13px] text-muted-foreground italic">{series.tagline}</p>

          <div className="mt-2 space-y-1.5">
            {isOffline && !isOnline ? (
              series.venue ? (
                <div className="text-[13px] text-muted-foreground flex items-start gap-1.5">
                  <MapPin size={12} className="mt-0.5 shrink-0" />
                  <span>
                    <span className="font-medium text-foreground/85">{series.venue.name}</span>
                    {series.venue.formattedAddress && (
                      <span className="block text-muted-foreground/80">{series.venue.formattedAddress}</span>
                    )}
                  </span>
                </div>
              ) : (
                <p className="text-[13px] text-muted-foreground flex items-center gap-1.5">
                  <MapPin size={12} /> In person
                </p>
              )
            ) : (
              <p className="text-[13px] text-muted-foreground flex items-center gap-1.5">
                <Video size={12} /> Online
              </p>
            )}
            <p className="text-[13px] text-muted-foreground flex items-center gap-1.5">
              <Users size={12} />
              {series.gatheringType.durationMinutes} min
            </p>
          </div>
        </div>

        <p className="text-[15px] text-foreground/80 leading-relaxed">{series.description}</p>

        {upcoming.length > 0 ? (
          <div className="space-y-1.5">
            <p className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground/80 flex items-center gap-1.5">
              <CalendarDays size={11} /> Upcoming sessions
            </p>
            <ul className="space-y-1">
              {upcoming.slice(0, 4).map((slot) => {
                const soldOut = isSlotSoldOut(slot);
                return (
                  <li
                    key={slot.id}
                    className={`text-[13px] flex items-center justify-between ${
                      soldOut ? 'text-muted-foreground/60' : 'text-foreground/80'
                    }`}
                  >
                    <span>{formatInstanceTime(slot.startsAt)}</span>
                    <span className={soldOut ? 'italic' : 'font-medium'}>{seatsLeftLabel(slot)}</span>
                  </li>
                );
              })}
              {upcoming.length > 4 && (
                <li className="text-[12px] text-muted-foreground/70 italic">
                  +{upcoming.length - 4} more
                </li>
              )}
            </ul>
          </div>
        ) : (
          <p className="text-[13px] text-muted-foreground italic">
            {series.nextGatheringStatus ?? 'No upcoming dates yet'}
          </p>
        )}
      </div>
    </div>
  );
}
